import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { BodyNormal } from '@atlassian/mpac-primitives';
import { MarketplaceListingStatus } from '@atlassian/mpac-types/graphql-gateway-types';
import { usePartnerPermissions } from '@atlassian/mpac-ui';
import { UrlUtils } from '@atlassian/mpac-utils';
import styled from '@emotion/styled';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { showOnlyPublicQueryParam } from '../../../../../../../constants';

export const SectionWrapper = styled.div`
  margin-bottom: 10px;
`;

export const BannerBody = styled(BodyNormal)({
  maxWidth: '57.5rem',
});

export const BannerMessage = {
  PrivateApp: () =>
    'This is a private app. No one can see this page except the contacts for this vendor and Marketplace administrators.',
  AppAwaitingApproval: () =>
    'This app is awaiting approval. Until it is approved, no one can see this page except contacts for this vendor and Marketplace administrators.',
  AppAwaitingRelease: () =>
    'This app is approved and awaiting release by the vendor. Until then, no one can see this page except contacts for this vendor and Marketplace administrators. A vendor contact can release this app on the Manage app page.',
  AppRejected: () => 'This app has been rejected by Atlassian, and is not available to the public.',
  ViewingWithPrivateData(publicAppListingUrl: string) {
    return (
      <>
        This app has a private version that is more recent than the last public version. You are
        currently viewing this page with private data included, which is not the same as what
        regular users will see. You can also choose to{' '}
        <Link to={publicAppListingUrl}>view public data only</Link>.
      </>
    );
  },
  ViewingPublicViewForPrivate(appListingUrl: string) {
    return (
      <>
        This app has a private version that is more recent than the last public version. You are
        currently viewing the public data only, which is what regular users will see. You can also
        choose to <Link to={appListingUrl}>view this page with private data included.</Link>
      </>
    );
  },
} as const;

export const PrivateListingBanner = () => {
  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  if (showAetherAppListingPage) {
    return <BannerBody>{BannerMessage.PrivateApp()}</BannerBody>;
  }

  return <SectionMessage>{BannerMessage.PrivateApp()}</SectionMessage>;
};

export const SubmittedListingBanner = () => {
  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  if (showAetherAppListingPage) {
    return <BannerBody>{BannerMessage.AppAwaitingApproval()}</BannerBody>;
  }

  return <SectionMessage>{BannerMessage.AppAwaitingApproval()}</SectionMessage>;
};

export const ReadyToLaunchListingBanner = ({ appManageLink }: { appManageLink: string }) => {
  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  if (showAetherAppListingPage) {
    return <BannerBody>{BannerMessage.AppAwaitingRelease()}</BannerBody>;
  }

  return (
    <SectionMessage
      actions={[
        <SectionMessageAction key="Manage app" href={appManageLink}>
          Manage app
        </SectionMessageAction>,
      ]}
    >
      {BannerMessage.AppAwaitingRelease()}
    </SectionMessage>
  );
};

export const RejectedListingBanner = () => {
  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  if (showAetherAppListingPage) {
    return <BannerBody>{BannerMessage.AppRejected()}</BannerBody>;
  }

  return <SectionMessage appearance="error">{BannerMessage.AppRejected()}</SectionMessage>;
};

export const PublicListingBanner = ({ appListingUrl }: { appListingUrl: string }) => {
  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  if (showAetherAppListingPage) {
    return <BannerBody>{BannerMessage.ViewingPublicViewForPrivate(appListingUrl)}</BannerBody>;
  }

  return (
    <SectionMessage>{BannerMessage.ViewingPublicViewForPrivate(appListingUrl)}</SectionMessage>
  );
};

export const PublicListingWithPrivateDataBanner = ({
  appListingPublicOnlyUrl,
}: {
  appListingPublicOnlyUrl: string;
}) => {
  const { enableAetherAppListingPage } = useStatsig();
  const showAetherAppListingPage = enableAetherAppListingPage();

  if (showAetherAppListingPage) {
    return <BannerBody>{BannerMessage.ViewingWithPrivateData(appListingPublicOnlyUrl)}</BannerBody>;
  }

  return (
    <SectionMessage>{BannerMessage.ViewingWithPrivateData(appListingPublicOnlyUrl)}</SectionMessage>
  );
};

export type Props = {
  appManageLink: string;
  hasNewerPrivateVersion: boolean;
  listingStatus: MarketplaceListingStatus;
  showOnlyPublic: boolean;
};

const AdminHeaderBanners = (props: Props) => {
  const permissions = usePartnerPermissions();
  const { listingStatus, appManageLink, hasNewerPrivateVersion, showOnlyPublic } = props;
  const canManageAppDetails = permissions?.canManageAppDetails ?? false;
  // short circuit if user cannot manage listing
  if (!canManageAppDetails) return null;

  const appListingUrl = UrlUtils.unsetQueryParam(window.location.href, showOnlyPublicQueryParam);
  const appListingPublicOnlyUrl = UrlUtils.updateQueryParam(
    window.location.href,
    showOnlyPublicQueryParam,
    'true'
  );

  let banner = null;
  switch (listingStatus) {
    case MarketplaceListingStatus.Private:
      // @ts-expect-error [MC-2850] - TS2322 - Type 'Element' is not assignable to type 'null'.
      banner = <PrivateListingBanner />;
      break;

    case MarketplaceListingStatus.Submitted:
      // @ts-expect-error [MC-2850] - TS2322 - Type 'Element' is not assignable to type 'null'.
      banner = <SubmittedListingBanner />;
      break;

    case MarketplaceListingStatus.ReadyToLaunch:
      // @ts-expect-error [MC-2850] - TS2322 - Type 'Element' is not assignable to type 'null'.
      banner = <ReadyToLaunchListingBanner appManageLink={appManageLink} />;
      break;

    case MarketplaceListingStatus.Rejected:
      // @ts-expect-error [MC-2850] - TS2322 - Type 'Element' is not assignable to type 'null'.
      banner = <RejectedListingBanner />;
      break;

    case MarketplaceListingStatus.Public:
      if (!hasNewerPrivateVersion) {
        banner = null;
      } else {
        // @ts-expect-error [MC-2850] - TS2322 - Type 'Element' is not assignable to type 'null'.
        banner = showOnlyPublic ? (
          <PublicListingBanner appListingUrl={appListingUrl} />
        ) : (
          <PublicListingWithPrivateDataBanner appListingPublicOnlyUrl={appListingPublicOnlyUrl} />
        );
      }
      break;
  }

  return banner && <SectionWrapper>{banner}</SectionWrapper>;
};

export default AdminHeaderBanners;
